import { ArrowLeft, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import AppHeader from '../components/AppHeader';
import StatusMessage from '../components/StatusMessage';
import ToolboxParticipantRow from '../components/ToolboxParticipantRow';
import useAmarreData from '../hooks/useAmarreData';
import AppPageLayout from '../layout/AppPageLayout';
import api from '../services/api';
import { useAuthStore } from '../store/authStore';
import { useAmarreStore } from '../store/amarreStore';
import { useEvaluationStore } from '../store/evaluationStore';

function AmarrePage() {
  const navigate = useNavigate();
  const session = useAuthStore((state) => state.session);
  const iniciarEvaluacion = useEvaluationStore((state) => state.iniciarEvaluacion);
  const creandoEvaluacion = useEvaluationStore((state) => state.creandoEvaluacion);
  const errorEvaluacion = useEvaluationStore((state) => state.errorEvaluacion);
  const successEvaluacion = useEvaluationStore((state) => state.successEvaluacion);
  const {
    tipoOperativos,
    operativos,
    lugares,
    participantes,
    loading,
    error,
    cargarTipoOperativos,
    cargarOperativos,
    cargarLugares,
    buscarParticipantes,
  } = useAmarreData();

  const [informesEnCurso, setInformesEnCurso] = useState([]);
  const [mostrarModalRetomar, setMostrarModalRetomar] = useState(false);
  const TRP_URL = process.env.REACT_APP_TRP_CLIENT_URL || 'https://opeva-clientes-trp-dev.jphlions.com';
  const SERVICE_TOKEN = process.env.REACT_APP_TRP_SERVICE_TOKEN || '';

  const filters = useAmarreStore((state) => state.filters);
  const hasSearchedOperarios = useAmarreStore(
    (state) => state.hasSearchedOperarios
  );
  const operariosDisponibles = useAmarreStore((state) => state.operariosDisponibles);
  const operarioSearch = useAmarreStore((state) => state.operarioSearch);
  const operariosSeleccionados = useAmarreStore(
    (state) => state.operariosSeleccionados
  );
  const setTipoOperativo = useAmarreStore((state) => state.setTipoOperativo);
  const setOperativo = useAmarreStore((state) => state.setOperativo);
  const setLugarOperativo = useAmarreStore(
    (state) => state.setLugarOperativo
  );
  const setDate = useAmarreStore((state) => state.setDate);
  const setTurno = useAmarreStore((state) => state.setTurno);
  const buscarOperarios = useAmarreStore((state) => state.buscarOperarios);
  const volverAFiltros = useAmarreStore((state) => state.volverAFiltros);
  const setOperarioSearch = useAmarreStore((state) => state.setOperarioSearch);
  const toggleOperario = useAmarreStore((state) => state.toggleOperario);

  useEffect(() => {
    cargarTipoOperativos();
  }, [cargarTipoOperativos]);

  const tipoOperativoOptions = [
    { value: '', label: 'Seleccione Tipo Operativo' },
    ...tipoOperativos.map(t => ({ value: t.id, label: t.Descripcion }))
  ];
  const operativoOptions = [
    { value: '', label: 'Seleccione Operativo' },
    ...operativos.map(o => ({ value: o.id, label: o.descript }))
  ];
  const lugarOperativoOptions = [
    { value: '', label: 'Seleccione Lugar Operativo' },
    ...lugares.map(l => ({ value: l.id, label: l.descript }))
  ];
  const turnoOptions = [
    { value: '', label: 'Seleccione Turno' },
    { value: '07:00', label: 'Mañana (07:00)' },
    { value: '15:00', label: 'Tarde (15:00)' },
    { value: '-01:00', label: 'Noche (-01:00)' },
  ];

  const filteredOperarios = useMemo(() => {
    const query = operarioSearch.trim().toLowerCase();

    if (!query) {
      return operariosDisponibles;
    }

    return operariosDisponibles.filter((item) =>
      `${item.displayName} ${item.legajo}`.toLowerCase().includes(query)
    );
  }, [operarioSearch, operariosDisponibles]);

  const selectedOperarioIds = useMemo(
    () => new Set(operariosSeleccionados.map((item) => item.id)),
    [operariosSeleccionados]
  );

  const handleBack = () => {
    if (hasSearchedOperarios) {
      volverAFiltros();
      return;
    }

    navigate('/dashboard');
  };

  // const handleSearchOperarios = async () => {
  //   const nextParticipantes = await buscarParticipantes({
  //     fecha: filters.date,
  //     turno: filters.turno,
  //     operativo: filters.operativo,
  //     lugarOperativo: filters.lugarOperativo,
  //   });
  //   buscarOperarios(nextParticipantes || participantes);
  // };

  const handleSearchOperarios = async () => {
    const nextParticipantes = await buscarParticipantes({
      fecha: filters.date,
      turno: filters.turno,
      operativo: filters.operativo,
      lugarOperativo: filters.lugarOperativo,
    });
    buscarOperarios(nextParticipantes || participantes);

    // Verificar si hay informes en curso
    try {
      const params = new URLSearchParams({
        tipoOperativo: filters.tipoOperativo,
        operativo: filters.operativo,
        lugarOperativo: filters.lugarOperativo,
        turno: filters.turno,
        fecha: filters.date,
        tenant: process.env.REACT_APP_TENANT_ID || 'tenant_a',
      });
      const resp = await fetch(`${TRP_URL}/amarre/informe-en-curso?${params}`, {
        headers: { 'x-service-token': SERVICE_TOKEN }
      });
      const data = await resp.json();
      if (data.success && data.data?.length > 0) {
        setInformesEnCurso(data.data);
        setMostrarModalRetomar(true);
      }
    } catch (err) {
      console.error('[AMARRE] Error verificando informes en curso:', err.message);
    }
  };

  const handleConfirm = async () => {
    if (!operariosSeleccionados.length || !session?.user?.id) {
      return;
    }

    try {
      // Sincronizar cada operario con entidades del tenant
      const operariosConEntidad = await Promise.all(
        operariosSeleccionados.map(async (operario) => {
          // Buscar entidad por legajo
          const busquedaResp = await api.get(
            `/api/evento_evaluacion/entidades?legajo=${encodeURIComponent(operario.legajo)}`
          );
          const entidades = busquedaResp.data || [];
          const entidadExacta = entidades.find(e => {
            try {
              const m = typeof e.metadata === 'string' ? JSON.parse(e.metadata) : e.metadata || {};
              return m.legajo === operario.legajo;
            } catch { return false; }
          });

          if (entidadExacta) {
            return { ...operario, id_entidad: entidadExacta.id };
          }

          // Crear entidad si no existe
          const createResp = await api.post('/api/evento_evaluacion/entidades', {
            id_tipo_entidad: 1,
            nombre: operario.nombre,
            descripcion: 'Sincronizado desde módulo de amarre',
            metadata: { legajo: operario.legajo }
          });
          const id_entidad = createResp.data;
          return { ...operario, id_entidad };
        })
      );

      const operarioPrincipal = operariosConEntidad[0];
      const firstQuestionId = await iniciarEvaluacion(
        'amarre',
        session.user.id,
        operarioPrincipal,
        {
          operariosEvaluados: operariosConEntidad.map((op) => ({
            id: op.id_entidad,
            id_entidad: op.id_entidad,
            nombre: op.nombre,
            legajo: op.legajo,
            displayName: op.nombre,
          })),
          amarreContext: {
            tipoOperativo: filters.tipoOperativo,
            operativo: filters.operativo,
            lugarOperativo: filters.lugarOperativo,
            turno: filters.turno,
            fecha: filters.date,
          },
        }
      );
      navigate(`/amarre/preguntas/${firstQuestionId}`);
    } catch (err) {
      console.error('[AMARRE] Error al confirmar:', err.message);
    }
  };

  const handleRetomar = async () => {
    setMostrarModalRetomar(false);
    // Agrupar por id_evento — tomar el más reciente
    const idEventoMasReciente = informesEnCurso[0]?.id_evento;
    const informesDelEvento = informesEnCurso.filter(i => i.id_evento === idEventoMasReciente);

    // Reconstruir evaluacionActual desde los informes en curso
    const primerInforme = informesDelEvento[0];

    // Obtener preguntas con respuestas del primer informe
    const itemsResp = await api.get(`/api/informes/items?evaluacion=${primerInforme.id_evaluacion}`);
    const items = itemsResp.data || [];
    const item = items[0];
    if (!item) return;

    const id_evidencia = item?.Item_Evaluacion?.[0]?.Evidencias?.[0]?.id ?? null;

    const id_item_evaluacion = item.id;
    const id_evaluacion = primerInforme.id_evaluacion;

    const preguntas = (item.Item_Evaluacion?.[0]?.Preguntas || []).map((p, idx) => ({
      id: String(p.id),
      preguntaId: p.id,
      id_item_evaluacion,
      id_evidencia,
      pregunta: p.titulo,
      respuesta: (() => {
        const r = p.Respuesta?.[0]?.respuesta;
        if (r === 'cumple') return 'si';
        if (r === 'no_cumple') return 'no';
        if (r === 'no_aplica') return 'no aplica';
        return '';
      })(),
      observacion: '',
      requiere_evidencia: false,
      evidencia: '',
      evidenciaPreview: '',
      orden: p.orden || idx + 1,
    }));

    const ctx = (() => {
      try {
        const meta = typeof primerInforme.evento_metadata === 'string'
          ? JSON.parse(primerInforme.evento_metadata)
          : primerInforme.evento_metadata || {};
        return meta.amarreContext || {};
      } catch { return {}; }
    })();

    const evaluacionActual = {
      id: primerInforme.id_evaluacion,
      id_informe: primerInforme.id_informe,
      id_evento: primerInforme.id_evento,
      tipo: 'amarre',
      responsableId: session?.user?.id,
      evaluadoId: primerInforme.id_entidad,
      nombreEvaluado: primerInforme.nombre_evaluado,
      legajoEvaluado: primerInforme.legajo_evaluado,
      examen: 'AMARRE',
      firma: '',
      operariosEvaluados: informesDelEvento.map(i => ({
        id: i.id_entidad,
        id_entidad: i.id_entidad,
        nombre: i.nombre_evaluado,
        legajo: i.legajo_evaluado,
      })),
      amarreContext: ctx,
      informesAmarre: informesDelEvento.map(i => ({
        id_informe: i.id_informe,
        id_evaluacion: i.id_evaluacion,
        id_item_evaluacion: item.id,
        id_evaluado: i.id_entidad,
        nombre: i.nombre_evaluado,
        legajo: i.legajo_evaluado,
      })),
      preguntas,
    };

    // Setear el store directamente
    const { useEvaluationStore: store } = await import('../store/evaluationStore');
    store.setState({ evaluacionActual });
    navigate(`/amarre/preguntas/${preguntas[0]?.id}`);

    (async () => {
      try {
        const evidenciasGuardadas = await api.fetchEvidenciasInforme(id_item_evaluacion);
        if (!evidenciasGuardadas.length) return;

        const descargadas = await Promise.all(
          evidenciasGuardadas.map(async (ev) => ({
            id_pregunta_item: ev.id_pregunta_item,
            referencia_archivo: ev.referencia_archivo,
            dataUrl: await api.fetchEvidenciaComoDataUrl(ev.referencia_archivo),
          }))
        );

        const previewPorPregunta = {};
        const referenciaPorPregunta = {};
        for (const { id_pregunta_item, referencia_archivo, dataUrl } of descargadas) {
          if (!dataUrl) continue;
          previewPorPregunta[String(id_pregunta_item)] = dataUrl;
          referenciaPorPregunta[String(id_pregunta_item)] = referencia_archivo;
        }

        if (!Object.keys(previewPorPregunta).length) return;

        const current = store.getState().evaluacionActual;
        if (!current || current.id !== id_evaluacion) return;

        store.setState({
          evaluacionActual: {
            ...current,
            preguntas: current.preguntas.map((p) => {
              const key = String(p.preguntaId ?? p.id);
              const dataUrl = previewPorPregunta[key];
              if (!dataUrl) return p;
              return {
                ...p,
                evidenciaPreview: dataUrl,
                evidencia: referenciaPorPregunta[key] || p.evidencia,
              };
            }),
          },
        });
      } catch {
        // no bloquear ni alterar la apertura del informe
      }
    })();
  };


  return (

    <AppPageLayout>
      <AppHeader />
      {mostrarModalRetomar && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-[400px] rounded-[18px] bg-white p-6 shadow-xl">
            <h2 className="font-login text-[18px] font-bold text-[#30343b]">
              Evaluación en curso
            </h2>
            <p className="mt-3 font-login text-[14px] text-[#5f6673]">
              Hay una evaluación de amarre en curso para estos filtros con{' '}
              <strong>{informesEnCurso.length} operario(s)</strong>. ¿Querés retomar o iniciar una nueva?
            </p>
            <div className="mt-6 flex flex-col gap-3">
              <button
                type="button"
                onClick={handleRetomar}
                className="w-full rounded-[10px] bg-[#2e86b5] py-3 font-login text-[15px] font-bold text-white"
              >
                Retomar evaluación
              </button>
              <button
                type="button"
                onClick={() => setMostrarModalRetomar(false)}
                className="w-full rounded-[10px] border border-[#d7dce2] py-3 font-login text-[15px] font-bold text-[#30343b]"
              >
                Iniciar nueva
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="mx-auto mt-10 w-full max-w-[820px] lg:mt-12">
        <h1 className="text-center font-login text-[30px] font-bold leading-tight text-[#30343b] sm:text-[36px]">
          Amarre
        </h1>

        <div className="mt-8 border-t border-[#eceff3] pt-6">
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full text-[#111111] transition hover:bg-[#f5f7fa]"
            aria-label="Volver"
          >
            <ArrowLeft className="h-5 w-5" strokeWidth={2.8} />
          </button>

          {!hasSearchedOperarios ? (
            <div className="mt-6 rounded-[22px] border border-[#dfe6ed] bg-white p-5 shadow-[0_16px_40px_rgba(15,23,42,0.08)] sm:p-6">
              <div className="grid gap-5 md:grid-cols-2">
                <div className="space-y-2">
                  <label
                    htmlFor="amarre-tipo-operativo"
                    className="block font-login text-[14px] font-bold text-[#30343b]"
                  >
                    Tipo Operativo
                  </label>
                  <select
                    id="amarre-tipo-operativo"
                    value={filters.tipoOperativo}
                    onChange={(event) => {
                      const val = event.target.value;
                      setTipoOperativo(val);
                      setOperativo('');
                      setLugarOperativo('');
                      if (val) {
                        cargarOperativos(val);
                        cargarLugares(val);
                      }
                    }}
                    className="h-11 w-full rounded-[10px] border border-[#cfd7df] bg-white px-4 font-login text-[14px] text-[#374151] outline-none transition focus:border-[#2e86b5]"
                  >
                    {tipoOperativoOptions.map((option) => (
                      <option key={option.value || 'placeholder'} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="amarre-operativo"
                    className="block font-login text-[14px] font-bold text-[#30343b]"
                  >
                    Operativo
                  </label>
                  <select
                    id="amarre-operativo"
                    value={filters.operativo}
                    onChange={(event) => setOperativo(event.target.value)}
                    disabled={!filters.tipoOperativo}
                    className="h-11 w-full rounded-[10px] border border-[#cfd7df] bg-white px-4 font-login text-[14px] text-[#374151] outline-none transition focus:border-[#2e86b5] disabled:cursor-not-allowed disabled:bg-[#f3f5f7] disabled:text-[#9aa3ad]"
                  >
                    {operativoOptions.map((option) => (
                      <option key={option.value || 'placeholder'} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="amarre-lugar-operativo"
                    className="block font-login text-[14px] font-bold text-[#30343b]"
                  >
                    Lugar Operativo
                  </label>
                  <select
                    id="amarre-lugar-operativo"
                    value={filters.lugarOperativo}
                    onChange={(event) => setLugarOperativo(event.target.value)}
                    disabled={!filters.operativo}
                    className="h-11 w-full rounded-[10px] border border-[#cfd7df] bg-white px-4 font-login text-[14px] text-[#374151] outline-none transition focus:border-[#2e86b5] disabled:cursor-not-allowed disabled:bg-[#f3f5f7] disabled:text-[#9aa3ad]"
                  >
                    {lugarOperativoOptions.map((option) => (
                      <option key={option.value || 'placeholder'} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="amarre-turno"
                    className="block font-login text-[14px] font-bold text-[#30343b]"
                  >
                    Turno
                  </label>
                  <select
                    id="amarre-turno"
                    value={filters.turno}
                    onChange={(event) => setTurno(event.target.value)}
                    className="h-11 w-full rounded-[10px] border border-[#cfd7df] bg-white px-4 font-login text-[14px] text-[#374151] outline-none transition focus:border-[#2e86b5]"
                  >
                    {turnoOptions.map((option) => (
                      <option key={option.value || 'placeholder'} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <label
                    htmlFor="amarre-date"
                    className="block font-login text-[14px] font-bold text-[#30343b]"
                  >
                    Fecha
                  </label>
                  <input
                    id="amarre-date"
                    type="date"
                    value={filters.date}
                    onChange={(event) => setDate(event.target.value)}
                    className="h-11 w-full rounded-[10px] border border-[#cfd7df] bg-white px-4 font-login text-[14px] text-[#374151] outline-none transition focus:border-[#2e86b5]"
                  />
                </div>
              </div>

              <button
                type="button"
                onClick={handleSearchOperarios}
                disabled={
                  !filters.tipoOperativo ||
                  !filters.operativo ||
                  !filters.lugarOperativo ||
                  !filters.turno ||
                  loading
                }
                className="mt-6 w-full rounded-[12px] bg-[#7f9db1] px-4 py-4 font-login text-[15px] font-bold text-white transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? 'Cargando operarios...' : 'Buscar Operarios'}
              </button>
            </div>
          ) : (
            <div className="mt-6 rounded-[22px] border border-[#dfe6ed] bg-white p-4 shadow-[0_16px_40px_rgba(15,23,42,0.08)] sm:p-6">
              <div className="flex overflow-hidden rounded-[10px] border border-[#d7dce2] bg-white shadow-sm">
                <input
                  type="text"
                  value={operarioSearch}
                  onChange={(event) => setOperarioSearch(event.target.value)}
                  placeholder="Legajo / Nombre"
                  className="h-11 flex-1 border-0 px-4 font-login text-[14px] text-[#374151] outline-none placeholder:text-[#9aa3ad]"
                />
                <button
                  type="button"
                  aria-label="Buscar operario"
                  className="flex h-11 w-12 items-center justify-center bg-[#14686a] text-white"
                >
                  <Search className="h-4 w-4" strokeWidth={2.8} />
                </button>
              </div>

              <div className="mt-4 overflow-hidden rounded-[12px] border border-[#dce2e8] bg-white">
                <div className="grid grid-cols-[1.5fr_0.9fr] gap-3 border-b border-[#dce2e8] bg-[#f7f8fa] px-3 py-3">
                  <span className="font-login text-[11px] font-bold text-[#30343b] sm:text-[12px]">
                    Nombre
                  </span>
                  <span className="font-login text-[11px] font-bold text-[#30343b] sm:text-[12px]">
                    Legajo
                  </span>
                </div>

                <div className="max-h-[340px] overflow-auto">
                  {error ? (
                    <p className="px-4 py-5 font-login text-[14px] text-[#c43c2f]">
                      No se pudo cargar la lista de operarios.
                    </p>
                  ) : null}

                  {!error && !filteredOperarios.length ? (
                    <p className="px-4 py-5 font-login text-[14px] text-[#5f6673]">
                      No hay operarios para mostrar.
                    </p>
                  ) : null}

                  {filteredOperarios.map((item) => (
                    <ToolboxParticipantRow
                      key={item.id}
                      nombre={item.displayName}
                      legajo={item.legajo}
                      isSelected={selectedOperarioIds.has(item.id)}
                      onClick={() => toggleOperario(item)}
                    />
                  ))}
                </div>
              </div>

              <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="font-login text-[14px] font-semibold text-[#526070]">
                  Seleccionados: {operariosSeleccionados.length}
                </p>

                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={
                    !operariosSeleccionados.length ||
                    creandoEvaluacion ||
                    !session?.user?.id
                  }
                  className="w-full rounded-[12px] bg-[#205f8d] px-4 py-4 font-login text-[15px] font-bold text-white transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50 sm:w-[220px]"
                >
                  {creandoEvaluacion ? 'Iniciando...' : 'Confirmar'}
                </button>
              </div>
            </div>
          )}

          <StatusMessage
            loading={creandoEvaluacion}
            error={errorEvaluacion}
            success={successEvaluacion}
          />
        </div>

        <div className="pt-10 text-center font-login text-[10px] text-[#5f6673]">
          <p>Copyright 2022 JPH Lions Todos los derechos reservados</p>
        </div>
      </div>
    </AppPageLayout>
  );
}

export default AmarrePage;
